"use client";
import {
  Combobox as HeadlessCombobox,
  ComboboxProps as HeadlessComboboxProps,
  ComboboxInput as HeadlessComboboxInput,
  ComboboxInputProps as HeadlessComboboxInputProps,
  ComboboxOptions as HeadlessComboboxOptions,
  ComboboxOption as HeadlessComboboxOption,
  Button as HeadlessButton,
} from "@headlessui/react";
import {
  RefObject,
  FC,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useDebouncedCallback } from "@/hooks";
import {
  type StandardFormFieldProps,
  StandardFormField,
  type RoundFormFieldProps,
  RoundFormField,
  type LargeRoundFormFieldProps,
  LargeRoundFormField,
} from "@/components/FormFieldRendering";
import { Search } from "@/icons/Search";
import { Dropdown } from "@/types/General/Dropdown";
import { XMarkThin } from "@/icons";
import { RectangularFormField } from "@/components/FormFieldRendering";
import { Link as LinkType } from "@/types";
import Link, { isInternalUrl } from "@/components/Link";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
// import { useRouter } from "next/navigation";

export type SearchDropdown = {
  type: "search";
} & Dropdown;

export type LinkDropdown = {
  type: "link";
} & LinkType;

type InternalSearchInputProps = Omit<HeadlessComboboxInputProps, "onChange"> & {
  value: string;
  suggestionsContainerClassname?: string;
  suggestionItemClassname?: string;
  suggestions?: (SearchDropdown | LinkDropdown)[];
  maxSuggestions?: number;
  onSearch?: () => void;
  onClose?: HeadlessComboboxProps<Dropdown, false>["onClose"];
  onChange?: (value: string) => void;
  searchIconAriaLabel?: string;
  resetSearchIconAriaLabel?: string;
  showSuggestionsWhileTyping?: boolean;
  iconsContainerClassname?: string;
  useAbsoluteDropdown?: boolean;
};

const BaseSearchInput: FC<InternalSearchInputProps> = ({
  value,
  suggestions,
  maxSuggestions,
  suggestionsContainerClassname,
  suggestionItemClassname,
  onSearch,
  onClose,
  onChange,
  searchIconAriaLabel,
  resetSearchIconAriaLabel,
  showSuggestionsWhileTyping,
  iconsContainerClassname,
  useAbsoluteDropdown,
  ...props
}) => {
  // const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBtnRef = useRef<HTMLButtonElement>(null);
  const hasNavigatedRef = useRef(false);
  const hasTypedRef = useRef(false);
  const isTabbingRef = useRef(false);
  const [hasNavigated, setHasNavigated] = useState(false);

  const [displayValue, setDisplayValue] = useState(value);
  const debouncedOnChange = useDebouncedCallback(
    (val: string) => onChange?.(val),
    300,
  );

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const renderLabel = useCallback(
    (label: string) => {
      if (!displayValue) return label;

      const escaped = displayValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`\\b${escaped}`, "i");
      const match = label.match(regex);

      if (!match || match.index === undefined) return label;

      const matchedText = match[0];
      const before = label.slice(0, match.index);
      const after = label.slice(match.index + matchedText.length);

      return (
        <span>
          {before}
          <strong>{matchedText}</strong>
          {after}
        </span>
      );
    },
    [displayValue],
  );

  // Deduplicate suggestions by title to avoid duplicate React keys
  const uniqueSuggestions = useMemo(() => {
    const seen = new Set<string>();
    return suggestions?.filter((suggestion) => {
      const key =
        suggestion.type === "link" ? suggestion.title : suggestion.value;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [suggestions]);

  return (
    <div className="contents">
      <HeadlessCombobox
        immediate
        value={value}
        onChange={(value) => {
          if (isTabbingRef.current) {
            isTabbingRef.current = false;
            return;
          }
          const selection = uniqueSuggestions?.find((suggestion) =>
            suggestion.type === "link"
              ? suggestion.title === value
              : suggestion.value === value,
          );
          if (value) {
            onChange?.(value);

            if (selection?.type === "link") {
              // router.push preserves state, resulting in two `main` elements
              // see https://nextjs.org/docs/app/guides/preserving-ui-state
              // wait until Next provides opt-out strategies
              // router.push(selection.href);
              if(isInternalUrl(selection.href)) { 
                location.href = selection.href;
              } else {
                window.open(selection.href, "_blank", "noopener,noreferrer");
              }
            }
          }
        }}
        onClose={() => {
          hasNavigatedRef.current = false;
          hasTypedRef.current = false;
          isTabbingRef.current = false;
          setHasNavigated(false);
          onClose?.();
        }}
      >
        <HeadlessComboboxInput
          ref={inputRef}
          {...getDataAutomationId("search-input")}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              if (!hasNavigatedRef.current && hasTypedRef.current) {
                // Typing triggers HeadlessUI auto-focus on first option;
                // prevent advancing past it on the first arrow press
                event.preventDefault();
              }
              hasNavigatedRef.current = true;
              setHasNavigated(true);
            }
            if (event.key === "Tab") {
              isTabbingRef.current = true;
            }
            if (event.key === "Enter" && !hasNavigatedRef.current) {
              event.preventDefault();
              searchBtnRef.current?.blur();
              onSearch?.();
            }
          }}
          onChange={(event) => {
            hasTypedRef.current = true;
            hasNavigatedRef.current = false;
            setHasNavigated(false);
            setDisplayValue(event.target.value);
            if (showSuggestionsWhileTyping) {
              onChange?.(event.target.value);
            } else {
              debouncedOnChange(event.target.value);
            }
          }}
          value={displayValue}
          {...props}
        />
        <div
          className={cn("flex flex-row gap-2 pr-4", iconsContainerClassname)}
        >
          <HeadlessButton
            ref={searchBtnRef}
            aria-label={searchIconAriaLabel}
            type="submit"
            onClick={() => {
              searchBtnRef.current?.blur();
              onSearch?.();
            }}
          >
            <Search aria-hidden type="icon-28" width={28} height={28} />
          </HeadlessButton>
          <HeadlessButton
            aria-label={resetSearchIconAriaLabel}
            type="reset"
            tabIndex={value ? 0 : -1}
            aria-hidden={!value}
            className={cn(!value && "pointer-events-none hidden")}
            onClick={(event) => {
              onChange?.("");
              onSearch?.();
              if (useAbsoluteDropdown) {
                inputRef.current?.blur();
                (event.currentTarget as HTMLButtonElement).blur();
              } else {
                searchBtnRef.current?.blur();
                inputRef.current?.focus();
              }
            }}
          >
            <XMarkThin className="-mt-px size-6" />
          </HeadlessButton>
        </div>
        {(showSuggestionsWhileTyping || displayValue === value) &&
          !!uniqueSuggestions?.length && (
            <HeadlessComboboxOptions
              anchor={useAbsoluteDropdown ? false : "bottom"}
              modal={false}
              transition
              className={cn(
                "border-swdc-gray-100 bg-swdc-white text-swdc-black-600 shadow-dropdown overflow-hidden border [--anchor-gap:var(--spacing-4)] [--anchor-max-height:340px] focus:outline-none",
                useAbsoluteDropdown
                  ? "absolute top-full -left-px z-10 w-[calc(100%+2px)]"
                  : value
                    ? "w-[calc(var(--input-width)+30px+16px+24px)] [--anchor-offset:34px]"
                    : "w-[calc(var(--input-width)+30px+16px)] [--anchor-offset:22px]",
                suggestionsContainerClassname,
              )}
            >
              {(maxSuggestions
                ? uniqueSuggestions?.slice(0, maxSuggestions)
                : uniqueSuggestions
              )?.map((suggestion) =>
                suggestion.type === "search" ? (
                  <HeadlessComboboxOption
                    className={cn(
                      "text-body-m text-swdc-black-600 data-disabled:text-swdc-gray-200 relative flex cursor-pointer items-center py-2 pr-12 pl-2 select-none data-disabled:cursor-not-allowed data-selected:not-data-disabled:font-bold lg:pr-13 lg:pl-4",
                      hasNavigated &&
                        "data-focus:not-data-disabled:outline-swdc-blue-400 data-selected:data-focus:not-data-disabled:bg-swdc-silver-200 data-active:data-focus:not-data-disabled:bg-swdc-gray-50 data-focus:not-data-disabled:outline-2 data-focus:not-data-disabled:-outline-offset-2",
                      suggestionItemClassname,
                    )}
                    key={`${suggestion.value}-${suggestion.label}`}
                    value={suggestion.value}
                    disabled={suggestion.disabled}
                    {...getDataAutomationId("search-dropdown-option")}
                  >
                    {useAbsoluteDropdown
                      ? renderLabel(suggestion.label)
                      : suggestion.label}
                  </HeadlessComboboxOption>
                ) : (
                  <HeadlessComboboxOption
                    as={Link}
                    key={`${suggestion.title}-${suggestion.href}`}
                    href={suggestion.href}
                    value={suggestion.title}
                    className={cn(
                      "text-body-m text-swdc-black-600 data-disabled:text-swdc-gray-200 relative flex cursor-pointer items-center py-2 pr-12 pl-2 select-none data-disabled:cursor-not-allowed data-selected:not-data-disabled:font-bold lg:pr-13 lg:pl-4",
                      hasNavigated &&
                        "data-focus:not-data-disabled:outline-swdc-blue-400 data-selected:data-focus:not-data-disabled:bg-swdc-silver-200 data-active:data-focus:not-data-disabled:bg-swdc-gray-50 data-focus:not-data-disabled:outline-2 data-focus:not-data-disabled:-outline-offset-2",
                      suggestionItemClassname,
                    )}
                    {...getDataLinkAttributes(suggestion)}
                    {...getDataAutomationId("search-dropdown-option")}
                  >
                    {useAbsoluteDropdown
                      ? renderLabel(suggestion.title)
                      : suggestion.title}
                  </HeadlessComboboxOption>
                ),
              )}
            </HeadlessComboboxOptions>
          )}
      </HeadlessCombobox>
    </div>
  );
};

export type SearchInputProps = {
  type: "search";
  ref?: RefObject<HTMLInputElement> | null;
} & (
  | ({
      variant: "form";
    } & Omit<StandardFormFieldProps<InternalSearchInputProps>, "formControl">)
  | ({
      variant: "round";
    } & Omit<RoundFormFieldProps<InternalSearchInputProps>, "formControl">)
  | ({
      variant: "large-round";
    } & Omit<LargeRoundFormFieldProps<InternalSearchInputProps>, "formControl">)
  | ({
      variant: "rectangular";
    } & Omit<RectangularFormField<InternalSearchInputProps>, "formControl">)
  | ({
      variant: "unstyled";
    } & InternalSearchInputProps)
);
/**
 * DO NOT USE!
 * USE THE FACADE COMPONENT `Input` INSTEAD.
 */
export const SearchInput: FC<SearchInputProps> = ({ variant, ...props }) => {
  switch (variant) {
    case "form":
      return (
        <StandardFormField<HTMLInputElement, InternalSearchInputProps>
          className="w-full outline-none"
          suggestionsContainerClassname="rounded-2xl"
          formControl={BaseSearchInput}
          withFixedLabel={true}
          {...props}
        />
      );
    case "round":
      return (
        <RoundFormField
          className="w-full outline-none"
          suggestionsContainerClassname="rounded-2xl"
          formControl={BaseSearchInput}
          {...props}
        />
      );
    case "large-round":
      return (
        <LargeRoundFormField
          className="w-full py-2 leading-5 outline-none"
          suggestionsContainerClassname={cn(
            "!border-swdc-black-600 !rounded-t-none !rounded-b-[28px] !border-t-0 pt-4 pb-4 !shadow-none",
          )}
          suggestionItemClassname="pl-8 lg:pl-8 !text-body-s !text-swdc-gray-400 !leading-5"
          showSuggestionsWhileTyping
          iconsContainerClassname="!pr-0 !gap-5 -mr-1"
          useAbsoluteDropdown
          formControl={BaseSearchInput}
          {...props}
        />
      );
    case "rectangular":
      return (
        <RectangularFormField
          className="w-full py-2 pr-2 pl-4 outline-none"
          formControl={BaseSearchInput}
          {...props}
        />
      );
    case "unstyled":
    default:
      return <BaseSearchInput {...props} />;
  }
};
